import React, { useMemo, useState } from "react";
import { formatInterconnectSegmentLabel } from "./interconnectsUtils";

export function InterconnectFieldRow({ label, hint, children }) {
  return (
    <div className="commercial-field-row">
      <label className="commercial-field-label">
        {label}
        {hint ? (
          <span className="project-detail-muted" style={{ display: "block", fontWeight: 400, fontSize: 12 }}>
            {hint}
          </span>
        ) : null}
      </label>
      <div className="commercial-field-value">{children}</div>
    </div>
  );
}

export function LinkedProjectPicker({ projects, selectedId, onChange, disabled }) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = [...projects].sort((a, b) =>
      String(a.work_order || "").localeCompare(String(b.work_order || ""), undefined, { numeric: true })
    );
    if (!q) return list.slice(0, 200);
    return list
      .filter((row) => {
        const hay = `${row.work_order || ""} ${row.name || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 200);
  }, [projects, query]);

  return (
    <div className="interconnects-project-picker">
      <div className="interconnects-project-picker-search">
        <input
          type="search"
          className="commercial-field-input"
          placeholder="Search expansion subsegments by work order…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
        />
      </div>
      {filtered.length === 0 ? (
        <p className="interconnects-muted interconnects-project-picker-empty">No subsegments match.</p>
      ) : (
        filtered.map((row) => (
          <label key={row.id} className="interconnects-project-row">
            <input
              type="radio"
              name="linked-project"
              checked={Number(selectedId) === Number(row.id)}
              onChange={() => onChange(row.id)}
              disabled={disabled}
            />
            <span>
              <strong>{formatInterconnectSegmentLabel(row)}</strong>
              {row.name ? (
                <span className="interconnects-project-row-meta"> — {row.name}</span>
              ) : null}
            </span>
          </label>
        ))
      )}
      {selectedId && !disabled ? (
        <div className="interconnects-project-picker-actions">
          <button type="button" className="interconnects-btn" onClick={() => onChange(null)}>
            Clear selection
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function SegmentGroupPicker({ segmentGroups, selectedBases, onChange, disabled }) {
  const [query, setQuery] = useState("");
  const selected = useMemo(() => new Set(selectedBases), [selectedBases]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = [...segmentGroups];
    if (!q) return list.slice(0, 200);
    return list
      .filter((group) => {
        const hay = `${group.title || ""} ${group.woBase || ""} ${group.segment || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 200);
  }, [segmentGroups, query]);

  const toggle = (woBase) => {
    if (disabled) return;
    const next = new Set(selected);
    if (next.has(woBase)) next.delete(woBase);
    else next.add(woBase);
    onChange([...next]);
  };

  return (
    <div className="interconnects-project-picker">
      <div className="interconnects-project-picker-search">
        <input
          type="search"
          className="commercial-field-input"
          placeholder="Search expansion segments…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
        />
      </div>
      {filtered.length === 0 ? (
        <p className="interconnects-muted interconnects-project-picker-empty">No segments match.</p>
      ) : (
        filtered.map((group) => {
          const woBase = group.woBase || group.key;
          return (
            <label key={woBase} className="interconnects-project-row">
              <input
                type="checkbox"
                checked={selected.has(woBase)}
                onChange={() => toggle(woBase)}
                disabled={disabled}
              />
              <span>{group.title || woBase}</span>
            </label>
          );
        })
      )}
    </div>
  );
}
